import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Circle, Marker, Polyline, Popup, Tooltip } from 'react-leaflet'
import { DivIcon } from 'leaflet'
import moment from 'moment'
import { Button, Icon as AntIcon } from 'antd'
import { BaseMap } from '../../components/Map'
import { TILE_LAYERS, getLeafletTileConfig, getTileLayerAttribution } from '../../util/mapTiles'
import { getBeaconDeviceId, getBeaconLatLong, getBeaconRssi, getBeaconStatus, getBeaconTimestamp } from './helpers'
import MapPlaybackControl from '../DeviceMap/MapPlaybackControl'

const defaultCenter = [20.5937, 78.9629]

// ── static icons ───────────────────────────────────────────────────────────────

const beaconIcon = new DivIcon({
  className: 'ble-beacon-marker',
  html: '<div class="ble-beacon-marker__pulse"></div><div class="ble-beacon-marker__dot"></div>',
  iconSize: [34, 34],
  iconAnchor: [17, 17],
})

function makeHistoryDotIcon(statusClass, isCurrent, isSelected) {
  const size = isCurrent ? 12 : 9
  const half = size / 2
  const extraClass = isSelected ? ' ble-history-dot--selected' : ''
  return new DivIcon({
    className: 'ble-beacon-history-marker',
    html: `<div class="ble-history-dot ble-history-dot--${statusClass}${isCurrent ? ' ble-history-dot--current' : ''}${extraClass}" style="width:${size}px;height:${size}px"></div>`,
    iconSize: [size, size],
    iconAnchor: [half, half],
  })
}

function getStatusClass(record) {
  const s = (getBeaconStatus(record) || '').toUpperCase()
  if (s === 'FOUND') return 'found'
  if (s === 'LOST') return 'lost'
  return 'active'
}

// ── RSSI circle ────────────────────────────────────────────────────────────────

function getRssiCircleProps(rssiRaw) {
  const rssi = Number(rssiRaw)
  if (!rssiRaw || Number.isNaN(rssi)) {
    return { radius: 35, color: '#faad14', fillColor: '#faad14', fillOpacity: 0.1, weight: 1.5 }
  }
  const clamped = Math.max(-100, Math.min(-40, rssi))
  const t = (clamped + 100) / 60          // 0=weakest, 1=strongest
  const radius = Math.round(60 - t * 50)  // 10m–60m
  const hue = Math.round(t * 120)         // 0°=red (weak), 120°=green (strong)
  return {
    radius,
    color: `hsl(${hue}, 90%, 42%)`,
    fillColor: `hsl(${hue}, 90%, 55%)`,
    fillOpacity: 0.1,
    weight: 1.5,
  }
}

// d = 10 ^ ((MeasuredPower − RSSI) / 10n), MeasuredPower = −60 dBm (RSSI at 1m), n = 4
function calcDistance(rssiRaw, txPower = -60, n = 4) {
  const rssi = Number(rssiRaw)
  if (!rssiRaw || Number.isNaN(rssi) || rssi === 0) return null
  const d = Math.pow(10, (txPower - rssi) / (10 * n))
  if (d < 1) return '<1 m'
  if (d >= 1000) return `${(d / 1000).toFixed(2)} km`
  return `${Math.round(d)} m`
}

// ── component ─────────────────────────────────────────────────────────────────

function BleBeaconMap({ asset, latestRecord, historyData = [], showHistory = false, detectorAsset = null, detectorAssetsMap = {}, selectedHistoryRecord = null, isMaximized = false, onToggleMaximize }) {
  const mapRef = useRef(null)
  const intervalRef = useRef(null)
  const prevFilteredRef = useRef(null)

  const [zoom, setZoom] = useState(18)
  const [tileLayerType, setTileLayerType] = useState('current')
  const [scrollWheelZoom, setScrollWheelZoom] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(
    typeof document !== 'undefined' && document.body
      ? document.body.classList.contains('dark-theme')
      : false
  )
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackIdx, setPlaybackIdx] = useState(null)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const [fleetTimeRange, setFleetTimeRange] = useState([0, 0])

  // dark-mode observer
  useEffect(() => {
    if (typeof document === 'undefined' || !document.body) return undefined
    const observer = new MutationObserver(() => setIsDarkMode(document.body.classList.contains('dark-theme')))
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  // tile config
  const resolvedTileUrl = useMemo(() => {
    const cfg = tileLayerType !== 'current' ? TILE_LAYERS[tileLayerType] : getLeafletTileConfig(isDarkMode)
    return cfg.url || undefined
  }, [tileLayerType, isDarkMode])

  const resolvedTileSubdomains = useMemo(() => {
    const cfg = tileLayerType !== 'current' ? TILE_LAYERS[tileLayerType] : getLeafletTileConfig(isDarkMode)
    return cfg.subdomains || undefined
  }, [tileLayerType, isDarkMode])

  // derive history points with positions
  const historyPoints = useMemo(() => {
    return (historyData || [])
      .map((record, index) => {
        const position = getBeaconLatLong(record)
        return position ? { record, position, key: `${getBeaconTimestamp(record) || index}-${index}` } : null
      })
      .filter(Boolean)
  }, [historyData])

  // adapt to fleet-data format so MapPlaybackControl can consume it
  const beaconFleetData = useMemo(() =>
    historyPoints.map(p => ({ ...p, timestamp: getBeaconTimestamp(p.record) })),
    [historyPoints]
  )

  // trimmed subset driven by the time-range slider
  const filteredFleetData = useMemo(() => {
    if (!beaconFleetData.length) return []
    const start = Math.max(0, Math.min(fleetTimeRange[0], beaconFleetData.length - 1))
    const end = Math.max(0, Math.min(fleetTimeRange[1], beaconFleetData.length - 1))
    return beaconFleetData.slice(start, end + 1)
  }, [beaconFleetData, fleetTimeRange])

  // reset everything when history data changes
  useEffect(() => {
    setIsPlaying(false)
    setPlaybackIdx(null)
    clearInterval(intervalRef.current)
    setFleetTimeRange([0, Math.max(0, beaconFleetData.length - 1)])
  }, [historyData])  // eslint-disable-line react-hooks/exhaustive-deps

  // animation interval — mirrors device-details playback logic
  useEffect(() => {
    clearInterval(intervalRef.current)
    if (!isPlaying || !filteredFleetData.length) return

    const isNewData = prevFilteredRef.current !== filteredFleetData
    prevFilteredRef.current = filteredFleetData
    if (isNewData) setPlaybackIdx(0)

    intervalRef.current = setInterval(() => {
      setPlaybackIdx(prev => {
        const next = (prev === null || prev < 0 ? 0 : prev) + 1
        if (next >= filteredFleetData.length - 1) {
          setIsPlaying(false)
          return filteredFleetData.length - 1
        }
        return next
      })
    }, Math.max(50, Math.round(500 / (playbackSpeed || 1))))

    return () => clearInterval(intervalRef.current)
  }, [filteredFleetData, playbackSpeed, isPlaying])

  const isAnimating = isPlaying || (playbackIdx !== null && playbackIdx >= 0)

  const displayedPoints = useMemo(() => {
    if (!isAnimating || playbackIdx === null) return filteredFleetData
    return filteredFleetData.slice(0, playbackIdx + 1)
  }, [filteredFleetData, isAnimating, playbackIdx])

  const latestPosition = useMemo(() => getBeaconLatLong(latestRecord || {}), [latestRecord])

  const center = latestPosition || (historyPoints[0] && historyPoints[0].position) || defaultCenter

  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current && mapRef.current.leafletElement) {
        mapRef.current.leafletElement.invalidateSize()
        if (center) mapRef.current.leafletElement.flyTo(center, latestPosition ? 18 : 12)
      }
    }, 250)
    return () => clearTimeout(timer)
  }, [center && center[0], center && center[1], latestPosition])  // eslint-disable-line react-hooks/exhaustive-deps

  // recalculate tile coverage after container resizes (maximize / restore)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current && mapRef.current.leafletElement) {
        mapRef.current.leafletElement.invalidateSize()
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [isMaximized])

  // zoom out to fit all history points when history is activated
  useEffect(() => {
    if (!showHistory || !mapRef.current || !mapRef.current.leafletElement) return
    const map = mapRef.current.leafletElement
    const timer = setTimeout(() => {
      if (historyPoints.length > 1) {
        const bounds = historyPoints.map(p => p.position)
        map.fitBounds(bounds, { padding: [50, 50] })
        setTimeout(() => { if (map.getZoom() > 2) map.zoomOut(1) }, 500)
      } else if (historyPoints.length === 1) {
        map.flyTo(historyPoints[0].position, 14)
      } else if (latestPosition) {
        map.flyTo(latestPosition, 14)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [showHistory, historyPoints.length])  // eslint-disable-line react-hooks/exhaustive-deps

  // pan to selected history record when clicked from table
  useEffect(() => {
    if (!selectedHistoryRecord || !mapRef.current || !mapRef.current.leafletElement) return
    const ts = String(getBeaconTimestamp(selectedHistoryRecord))
    const point = historyPoints.find(p => String(getBeaconTimestamp(p.record)) === ts)
    if (!point) return
    mapRef.current.leafletElement.panTo(point.position)
  }, [selectedHistoryRecord])  // eslint-disable-line react-hooks/exhaustive-deps

  // pan to current animation point during playback
  useEffect(() => {
    if (playbackIdx === null || playbackIdx < 0 || !mapRef.current || !mapRef.current.leafletElement) return
    const point = filteredFleetData[playbackIdx]
    if (!point || !point.position) return
    mapRef.current.leafletElement.panTo(point.position, { animate: true, duration: 0.4 })
  }, [playbackIdx])  // eslint-disable-line react-hooks/exhaustive-deps

  const handleZoomChange = () => {
    if (mapRef.current && mapRef.current.leafletElement) {
      setZoom(mapRef.current.leafletElement.getZoom())
    }
  }

  const formatRangeTime = (ts) => ts ? moment(Number(ts)).format('DD-MM HH:mm:ss') : '-'

  return (
    <div
      className="ble-beacon-map"
      onClick={() => setScrollWheelZoom(true)}
      onMouseLeave={() => setScrollWheelZoom(false)}
    >
      {/* maximize / restore button */}
      {typeof onToggleMaximize === 'function' && (
        <button
          className="device-map-maximize-btn"
          onClick={onToggleMaximize}
          title={isMaximized ? 'Restore map' : 'Maximize map'}
        >
          <AntIcon type={isMaximized ? 'fullscreen-exit' : 'fullscreen'} />
        </button>
      )}

      {/* tile switcher */}
      <div className="device-map-tile-switcher">
        {Object.keys(TILE_LAYERS).map((key) => (
          <button
            key={key}
            className={`device-map-tile-btn${tileLayerType === key ? ' device-map-tile-btn-active' : ''}`}
            onClick={() => setTileLayerType(key)}
            title={TILE_LAYERS[key].label}
          >
            {TILE_LAYERS[key].label}
          </button>
        ))}
      </div>

      <BaseMap
        mapRef={mapRef}
        className="ble-beacon-leaflet"
        style={{ height: '100%', width: '100%' }}
        center={center}
        zoom={latestPosition ? 18 : 12}
        scrollWheelZoom={scrollWheelZoom}
        onZoomEnd={handleZoomChange}
        tileLayerUrl={resolvedTileUrl}
        tileLayerMaxZoom={TILE_LAYERS[tileLayerType].maxZoom || 22}
        tileLayerSubdomains={resolvedTileSubdomains}
        tileLayerAttribution={getTileLayerAttribution(tileLayerType)}
        geoJsonLabelsZoom={zoom}
        geoJsonLabelsMinZoom={12}
        geoFenceOverlayOrganisationId={asset && asset.organisation && (asset.organisation._id || asset.organisation)}
        geoFenceOverlayAsset={asset}
        geoFenceOverlayInteractive={false}
      >
        {/* route polyline connecting history points in order */}
        {showHistory && displayedPoints.length > 1 && (
          <Polyline
            positions={displayedPoints.filter(p => p.position).map(p => p.position)}
            color="#1890ff"
            weight={2.5}
            opacity={0.75}
          />
        )}

        {/* history circles + markers */}
        {showHistory && displayedPoints.map(({ record, position, key }, idx) => {
          const isCurrent = isAnimating && idx === displayedPoints.length - 1
          const statusClass = getStatusClass(record)
          const rssi = getBeaconRssi(record)
          const circleProps = getRssiCircleProps(rssi)
          const ts = getBeaconTimestamp(record)
          const deviceId = getBeaconDeviceId(record)
          const distance = calcDistance(rssi)
          const detectorDisplay = detectorAssetsMap[deviceId] ? detectorAssetsMap[deviceId].display : null
          const isSelected = selectedHistoryRecord && String(getBeaconTimestamp(selectedHistoryRecord)) === String(ts)
          const icon = makeHistoryDotIcon(statusClass, isCurrent, isSelected)
          return (
            <React.Fragment key={key}>
              <Circle
                center={position}
                radius={circleProps.radius}
                color={circleProps.color}
                fillColor={circleProps.fillColor}
                fillOpacity={circleProps.fillOpacity}
                weight={circleProps.weight}
              />
              <Marker position={position} icon={icon}>
                <Tooltip key={`tt-${key}-${isCurrent ? 'cur' : 'idle'}`} direction="top" opacity={1} sticky={!isSelected && !isCurrent} permanent={!!isSelected || isCurrent}>
                  <div className="ble-beacon-history-tooltip">
                    <div className="ble-beacon-history-tooltip__time">
                      {ts ? moment(Number(ts)).format('DD-MM-YYYY HH:mm:ss') : '-'}
                    </div>
                    <div>
                      <span className={`ble-beacon-history-tooltip__status ble-beacon-history-tooltip__status--${statusClass}`}>
                        {(getBeaconStatus(record) || '').toUpperCase()}
                      </span>
                    </div>
                    <div className="ble-beacon-history-tooltip__row">
                      <AntIcon type="mobile" /> {deviceId}
                    </div>
                    {detectorDisplay && (
                      <div className="ble-beacon-history-tooltip__row ble-beacon-history-tooltip__detector">
                        <AntIcon type="laptop" /> {detectorDisplay}
                      </div>
                    )}
                    <div className="ble-beacon-history-tooltip__row">
                      <AntIcon type="wifi" /> RSSI: {rssi}
                      {distance && <span className="ble-beacon-history-tooltip__dist"> ≈ {distance}</span>}
                    </div>
                  </div>
                </Tooltip>
              </Marker>
            </React.Fragment>
          )
        })}

        {/* latest beacon marker */}
        {latestPosition && (
          <Marker position={latestPosition} icon={beaconIcon}>
            <Popup>
              <div className="ble-beacon-history-tooltip">
                <div className="ble-beacon-history-tooltip__time">
                  {latestRecord && getBeaconTimestamp(latestRecord)
                    ? moment(Number(getBeaconTimestamp(latestRecord))).format('DD-MM-YYYY HH:mm:ss')
                    : '-'}
                </div>
                <div className="ble-beacon-history-tooltip__row">
                  <AntIcon type="mobile" /> {getBeaconDeviceId(latestRecord)}
                </div>
                {detectorAsset && (
                  <div className="ble-beacon-history-tooltip__row ble-beacon-history-tooltip__detector">
                    <AntIcon type="laptop" /> {detectorAsset.display}
                  </div>
                )}
                <div className="ble-beacon-history-tooltip__row">
                  <AntIcon type="wifi" /> RSSI: {getBeaconRssi(latestRecord)}
                  {calcDistance(getBeaconRssi(latestRecord)) && (
                    <span className="ble-beacon-history-tooltip__dist">
                      {' '}≈ {calcDistance(getBeaconRssi(latestRecord))}
                    </span>
                  )}
                </div>
              </div>
            </Popup>
            {!isAnimating && (
              <Tooltip key="asset-label-tooltip" direction="bottom" offset={[0, 18]} opacity={1} permanent>
                <h4>
                  {asset
                    ? [asset.assetCode, asset.ownerCode].filter(Boolean).join(' / ') || 'BLE Beacon'
                    : 'BLE Beacon'}
                </h4>
              </Tooltip>
            )}
          </Marker>
        )}
      </BaseMap>

      {/* overlays when history is active */}
      {showHistory && historyPoints.length > 0 && (
        <>
          {/* status legend */}
          <div className="ble-beacon-status-legend">
            <span className="ble-beacon-legend-dot ble-beacon-legend-dot--found" />Found
            <span className="ble-beacon-legend-dot ble-beacon-legend-dot--lost" />Lost
            <span className="ble-beacon-legend-dot ble-beacon-legend-dot--active" />Active
          </div>

          {/* fit-bounds button — raised to clear the playback control */}
          <Button
            size="small"
            className="ble-beacon-map-fit"
            style={{ bottom: 64 }}
            onClick={() => {
              if (!mapRef.current || !mapRef.current.leafletElement) return
              mapRef.current.leafletElement.fitBounds(historyPoints.map(p => p.position), { padding: [30, 30] })
            }}
          >
            Fit History
          </Button>
        </>
      )}

      {/* shared playback control bar */}
      <MapPlaybackControl
        visible={showHistory && beaconFleetData.length > 1}
        fleetRangeData={beaconFleetData}
        fleetTimeRange={fleetTimeRange}
        onTimeRangeChange={setFleetTimeRange}
        filteredFleetRangeData={filteredFleetData}
        currentIndex={playbackIdx === null ? 0 : Math.max(0, playbackIdx)}
        formatRangeTime={formatRangeTime}
        playbackSpeed={playbackSpeed}
        onSpeedChange={setPlaybackSpeed}
        isPlaying={isPlaying}
        onPlayPause={() => setIsPlaying(p => !p)}
        onReplay={() => { setPlaybackIdx(0); setIsPlaying(true); }}
        onStepBack={() => {
          setIsPlaying(false)
          setPlaybackIdx(prev => Math.max(0, (prev === null ? 0 : prev) - 1))
        }}
        onStep={() => {
          setIsPlaying(false)
          setPlaybackIdx(prev => Math.min((prev === null ? 0 : prev) + 1, filteredFleetData.length - 1))
        }}
      />
    </div>
  )
}

export default BleBeaconMap
